import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, XCircle, AlertCircle, FileText, Sparkles, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import FeedbackModal from './FeedbackModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  HIGH: 'red',
  MEDIUM: 'amber',
  LOW: 'slate'
};

const SUGGESTION_TYPE_LABELS = {
  MISSING_DOCUMENT: 'Manglende dokument',
  CREATE_TASK: 'Opprett oppgave',
  ADD_TIMELINE_EVENT: 'Legg til hendelse',
  UPDATE_FIELD: 'Oppdater felt',
  ADD_ANNOTATION: 'Legg til merknad',
  RISK_ALERT: 'Risikovarsel'
};

const AgentSuggestionCard = ({ suggestion, onUpdate }) => {
  const [processing, setProcessing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [comment, setComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/agents/suggestions/${suggestion.id}/approve`,
        { comment: comment || undefined }
      );
      
      toast.success('Forslag godkjent!', {
        description: response.data.implementation_result?.task_title || 'Handling utført'
      });
      
      setComment('');
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error approving suggestion:', error);
      toast.error('Kunne ikke godkjenne forslag');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Vennligst oppgi en grunn');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(
        `${API}/agents/suggestions/${suggestion.id}/reject`,
        { reason: rejectReason }
      );
      
      toast.success('Forslag avvist');
      
      setRejectReason('');
      setShowRejectModal(false);
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
      toast.error('Kunne ikke avvise forslag');
    } finally {
      setProcessing(false);
    }
  };

  const priority = suggestion.data?.priority || 'MEDIUM';
  const priorityColor = PRIORITY_COLORS[priority];

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-gray-600">
                {SUGGESTION_TYPE_LABELS[suggestion.suggestion_type] || suggestion.suggestion_type}
              </span>
              <span className={`px-2 py-0.5 text-xs font-medium bg-${priorityColor}-50 text-${priorityColor}-700 rounded-full`}>
                {priority}
              </span>
            </div>
            
            <h4 className="font-semibold text-gray-900 text-base">
              {suggestion.data?.task_title || suggestion.data?.title || suggestion.data?.document_name}
            </h4>
          </div>
          
          <div className="flex items-center gap-1 ml-3">
            <div className="text-right">
              <div className="text-xs text-gray-500">AI Tillit</div>
              <div className="text-sm font-semibold text-violet-600">
                {Math.round(suggestion.confidence_score * 100)}%
              </div>
            </div>
            <Sparkles className="w-4 h-4 text-violet-600" />
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-700 mb-3">
          {suggestion.data?.task_description || suggestion.data?.description || suggestion.data?.reason}
        </p>

        {/* Reasoning - AI accent with violet */}
        {suggestion.reasoning && (
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs font-medium text-violet-700 mb-1">AI Begrunnelse</div>
                <p className="text-xs text-gray-700">{suggestion.reasoning}</p>
              </div>
            </div>
          </div>
        )}

        {/* Additional Details */}
        {suggestion.data?.suggested_source && (
          <div className="text-xs text-gray-600 mb-3">
            <span className="font-medium">Kilde:</span> {suggestion.data.suggested_source}
          </div>
        )}

        {/* Comment Input */}
        <div className="mb-3">
          <label className="text-xs font-medium text-gray-700 mb-1 block">
            Kommentar (valgfritt)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Legg til dine egne notater..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
            rows={2}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            onClick={handleApprove}
            disabled={processing}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Godkjenn
          </Button>
          
          <Button
            onClick={() => setShowRejectModal(true)}
            disabled={processing}
            variant="outline"
            className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Avvis
          </Button>
        </div>

        {/* Feedback Button */}
        <div className="mt-2">
          <button
            onClick={() => setShowFeedbackModal(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-indigo-600 hover:bg-gray-50 border border-gray-200 hover:border-indigo-300 rounded-lg transition-all"
          >
            <MessageSquare className="w-4 h-4" />
            Gi tilbakemelding
          </button>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          agentRunId={suggestion.agent_run_id}
          suggestionId={suggestion.id}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={() => {
            // Optional: refresh suggestions after feedback
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Avvis forslag</h3>
            
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Hvorfor avviser du dette forslaget?
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Oppgi grunn..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              rows={3}
              autoFocus
            />
            
            <div className="flex gap-2">
              <Button
                onClick={handleReject}
                disabled={processing || !rejectReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  'Bekreft avvisning'
                )}
              </Button>
              <Button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                disabled={processing}
                variant="outline"
              >
                Avbryt
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentSuggestionCard;
